import React, { useEffect, useState } from 'react';
import { 
  Box,
  Text,
  SimpleGrid,
  Flex,
  Badge,
  Button,
  Heading,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  VStack,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Image,
  Portal,
  useToast,
  useBreakpointValue
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFriendsList, removeFriend } from '../../Redux/Login/friendsActions';
import { FaUserMinus, FaUserFriends, FaCoins, FaComment } from 'react-icons/fa';
import { AddIcon } from '@chakra-ui/icons';
import SendMessageModal from '../Messages/SendMessageModal';
import GiftCreditsModal from '../Credits/GiftCreditsModal';
import BurrowSpinner from '../../componants/UI/BurrowSpinner';
import BurrowAvatar from '../../componants/UI/BurrowAvatar';
import { openCommunityModal } from '../../utils/communityModalEvents';
import { MemberCard } from '../../componants/Feed/SharedMemberComponents';
import MemberProfileCard from '../../componants/Feed/MemberProfileCard';
import { addFriend } from '../../Redux/Login/friendsActions';
import { openReportMessageModal } from '../../utils/communityModalEvents';

const FriendsListTab = () => {
  const dispatch = useDispatch();
  const { items, loading, error } = useSelector(state => state.auth.friendsList);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Message & gift modals
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [giftRecipient, setGiftRecipient] = useState(null);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  
  // Avatar preview modal state
  const [previewAvatar, setPreviewAvatar] = useState(null);
  const [previewUserName, setPreviewUserName] = useState("");
  const avatarPreviewModal = useDisclosure();
  
  // MemberProfileCard overlay (mobile)
  const [selectedMember, setSelectedMember] = useState(null);
  const toast = useToast();
  
  // Responsive — true on mobile/tablet
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  useEffect(() => {
    dispatch(fetchFriendsList());
  }, [dispatch]);
  
  const handleRemoveFriend = async () => {
    if (selectedFriend) {
      const result = await dispatch(removeFriend(selectedFriend.friendAddId));
      if (result.success) {
        onClose();
      }
    }
  };
  
  const openRemoveModal = (friend) => {
    setSelectedFriend(friend);
    onOpen();
  };
  
  const handleMessageFriend = (friend) => {
    setMessageRecipient(friend.friendBurrowerId);
    setIsMessageModalOpen(true);
  };
  
  const handleGiftCredits = (friend) => {
    setGiftRecipient(friend.friendBurrowerId);
    setIsGiftModalOpen(true);
  };
  
  const handleAvatarPreview = (friend) => {
    setPreviewAvatar(friend.userAvatar || "https://bunny-burrow-pull-1.b-cdn.net/user-profile-images/image%20(16).png");
    setPreviewUserName(friend.friendBurrowerId);
    avatarPreviewModal.onOpen();
  };

  // --- MemberProfileCard overlay handlers (same as UserSearchResults) ---
  const handleProfileMessage = (burrowerId) => {
    setSelectedMember(null);
    setTimeout(() => {
      setMessageRecipient(burrowerId);
      setIsMessageModalOpen(true);
    }, 100);
  };

  const handleProfileAddToKin = async (burrowerId) => {
    // Already kin — this shouldn't fire from Kin tab, but handle gracefully
    toast({
      title: 'Already Kin',
      description: `${burrowerId} is already in your Kin list.`,
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
  };

  const handleProfileGiftMU = (burrowerId) => {
    setSelectedMember(null);
    setTimeout(() => {
      setGiftRecipient(burrowerId);
      setIsGiftModalOpen(true);
    }, 100);
  };

  const handleProfileReport = (burrowerId) => {
    setSelectedMember(null);
    setTimeout(() => {
      openReportMessageModal({
        senderName: burrowerId,
        messageId: `user-${burrowerId}`
      });
    }, 100);
  };

  // Convert friend data to member shape for MemberCard
  const friendToMember = (friend) => ({
    ...friend,
    burrowerId: friend.friendBurrowerId,
    userAvatar: friend.userAvatar,
    membershipTier: friend.membershipTier || (friend.accountType === 'free' ? 'base' : friend.accountType || 'base'),
    accountLevel: friend.accountLevel || 1,
    respectCount: friend.respectCount || 0,
    isOnline: friend.isOnline || false,
  });

  const handleMemberCardClick = (member) => {
    setSelectedMember(prev => prev?.burrowerId === member.burrowerId ? null : member);
  };
  
  // Render loading state
  if (loading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <BurrowSpinner size="xl" />
      </Flex>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertTitle mr={2}>Error!</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Render empty state
  if (!items || items.length === 0) {
    return (
      <Box 
        p={8} 
        textAlign="center" 
        borderRadius="xl"
        bg="gray.700"
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.05)"
        mx="auto"
        maxW="600px"
        borderWidth="1px"
        borderColor="rgba(236, 201, 75, 0.4)"
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "8px",
          bgGradient: "linear(to-r, yellow.400, orange.400, yellow.500)",
          borderTopRadius: "xl"
        }}
      >
        <Box 
          fontSize="6xl"
          color="yellow.400"
          mx="auto"
          mb={4}
          position="relative"
          width="fit-content"
          animation="float 3s ease-in-out infinite"
          sx={{
            "@keyframes float": {
              "0%": { transform: "translateY(0px)" },
              "50%": { transform: "translateY(-10px)" },
              "100%": { transform: "translateY(0px)" }
            }
          }}
        >
          <FaUserFriends />
        </Box>
        <Heading 
          size="lg" 
          mb={4}
          bgGradient="linear(to-r, yellow.400, orange.400, yellow.500)"
          bgClip="text"
          color="white"
        >
          No Kin Added Yet
        </Heading>
        <Text 
          color="white" 
          fontSize="lg" 
          maxW="400px" 
          mx="auto" 
          mb={6}
        >
          Your Kin list is empty. Find and connect with other users through the User Finder tab.
        </Text>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="yellow"
          size="lg"
          borderRadius="full"
          px={8}
          _hover={{
            transform: "translateY(-3px)",
            shadow: "xl"
          }}
          transition="all 0.3s"
          onClick={() => openCommunityModal({ tabIndex: 1 })}
        >
          Find Kin
        </Button>
      </Box>
    );
  }

  // Render friends list
  return (
    <Box p={{ base: 2, md: 5 }}>
      <Flex 
        align="center" 
        justify="space-between" 
        mb={{ base: 3, md: 6 }} 
        pb={{ base: 2, md: 4 }}
        borderBottom="1px solid"
        borderColor="yellow.300"
      >
        <Flex align="center">
          <Box 
            mr={3} 
            color="yellow.400" 
            fontSize="2xl"
          >
            <FaUserFriends />
          </Box>
          <Heading 
            size="md"
            bgGradient="linear(to-r, yellow.400, orange.400, yellow.500)"
            bgClip="text"
            fontWeight="bold"
          >
            Your Kin ({items.length})
          </Heading>
        </Flex>
        
        <Badge 
          colorScheme="yellow" 
          fontSize="sm" 
          px={3} 
          py={1} 
          borderRadius="full"
          boxShadow="sm"
          cursor="default"
        >
          Total: {items.length}
        </Badge>
      </Flex>

      {/* === MOBILE: MemberCard grid (same as search) with gold gradient overlay === */}
      {isMobile ? (
        <>
          <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={3}>
            {items.map(friend => {
              const member = friendToMember(friend);
              return (
                <MemberCard
                  key={friend.friendAddId}
                  member={member}
                  onClick={handleMemberCardClick}
                  isKin={true}
                />
              );
            })}
          </SimpleGrid>

          {/* MemberProfileCard Portal overlay */}
          {selectedMember && (
            <Portal>
              <Box
                position="fixed"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="blackAlpha.700"
                zIndex={1400}
                onClick={() => setSelectedMember(null)}
              />
              <Flex
                position="fixed"
                top={0}
                left={0}
                right={0}
                bottom={0}
                zIndex={1401}
                align="center"
                justify="center"
                pointerEvents="none"
              >
                <Box
                  pointerEvents="auto"
                  maxW="400px"
                  w="90vw"
                  maxH="90vh"
                  overflowY="auto"
                  css={{
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-thumb': { background: '#4A5568', borderRadius: '4px' }
                  }}
                >
                  <MemberProfileCard
                    member={selectedMember}
                    onClose={() => setSelectedMember(null)}
                    onMessage={handleProfileMessage}
                    onAddToKin={handleProfileAddToKin}
                    onGiftMU={handleProfileGiftMU}
                    onReport={handleProfileReport}
                    isKin={true}
                  />
                </Box>
              </Flex>
            </Portal>
          )}
        </>
      ) : (
        /* === DESKTOP: Keep original card layout === */
        <SimpleGrid columns={{ base: 1, sm: 1, md: 2, lg: 3 }} spacing={6}>
          {items.map(friend => (
            <Box 
              key={friend.friendAddId}
              p={4}
              bg="gray.700"
              borderWidth="1px"
              borderColor="rgba(236, 201, 75, 0.4)"
              borderRadius="lg"
              shadow="md"
              minW="280px"
              maxW="280px"
              position="relative"
              transition="all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
              _hover={{ 
                transform: "translateY(-5px)",
                shadow: "xl",
                borderColor: "yellow.300"
              }}
              _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "60px",
                bgGradient: "linear(to-r, yellow.400, orange.400, yellow.500)",
                borderTopRadius: "lg",
                opacity: 0.7,
                zIndex: 0
              }}
            >
              <Flex direction="column" align="center" position="relative">
                <Box
                  position="absolute"
                  top="-7px"
                  left="50%"
                  transform="translateX(-50%)"
                  width="80px"
                  height="5px"
                  bgGradient="linear(to-r, yellow.300, orange.300, yellow.300)"
                  borderRadius="full"
                  opacity="0.8"
                />
                
                <Box
                  cursor="pointer"
                  borderRadius="full"
                  transition="all 0.3s"
                  _hover={{ transform: "scale(1.08)" }}
                  onClick={() => handleAvatarPreview(friend)}
                  position="relative"
                  role="group"
                  mt={2}
                  mb={4}
                >
                  <BurrowAvatar 
                    size="xl" 
                    src={friend.userAvatar} 
                    name={friend.friendBurrowerId}
                    border="3px solid"
                    borderColor="yellow.400"
                    boxShadow="0 0 15px rgba(236, 201, 75, 0.5)"
                  />
                  <Box
                    position="absolute"
                    bottom="-5px"
                    left="50%"
                    transform="translateX(-50%)"
                    bg="blackAlpha.700"
                    color="white"
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="md"
                    opacity="0"
                    _groupHover={{ opacity: "1" }}
                    transition="opacity 0.2s"
                    pointerEvents="none"
                    width="auto"
                    whiteSpace="nowrap"
                  >
                    View Profile
                  </Box>
                </Box>
                
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="center"
                  width="100%"
                >
                  <Text 
                    fontWeight="bold" 
                    fontSize="lg"
                    isTruncated 
                    maxW="100%"
                    color="white"
                  >
                    {friend.friendBurrowerId}
                  </Text>
                  
                  <Badge 
                    colorScheme={friend.accountType === 'premium' ? 'purple' : 'gray'}
                    mb={2}
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    textTransform="capitalize"
                    boxShadow="0 0 5px rgba(128, 90, 213, 0.3)"
                  >
                    {friend.accountType}
                  </Badge>
                  <Box
                    bg="gray.800"
                    borderRadius="md"
                    p={4}
                    minH="70px"
                    width="100%"
                    mb={4}
                    textAlign="center"
                    boxShadow="inner"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="gray.700"
                  >
                    <Text 
                      fontSize="sm" 
                      color="gray.200" 
                      noOfLines={3}
                      lineHeight="1.5"
                      fontFamily="'Segoe UI', Candara, 'Trebuchet MS', sans-serif"
                      letterSpacing="0.3px"
                    >
                      {friend.userBio || "No bio available"}
                    </Text>
                  </Box>
                  
                  <Text 
                    fontSize="xs" 
                    color="gray.300" 
                    mb={4}
                    bg="gray.700"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    Added: {new Date(friend.dateAdded).toLocaleDateString()}
                  </Text>

                  <VStack width="100%" spacing={2}>
                    <Button 
                      leftIcon={<FaComment />}
                      bg="gray.700" 
                      color="white"
                      size="sm"
                      onClick={() => handleMessageFriend(friend)}
                      borderRadius="full"
                      width="100%"
                      px={4}
                      _hover={{ 
                        transform: "translateY(-2px)", 
                        boxShadow: "md",
                        bg: "gray.600"
                      }}
                      transition="all 0.2s"
                    >
                      Message
                    </Button>
                    
                    <Button 
                      leftIcon={<FaCoins />}
                      bg="gray.700"
                      color="yellow.300"
                      size="sm"
                      onClick={() => handleGiftCredits(friend)}
                      borderRadius="full"
                      width="100%"
                      px={4}
                      _hover={{ 
                        transform: "translateY(-2px)", 
                        boxShadow: "md",
                        bg: "gray.600"
                      }}
                      transition="all 0.2s"
                    >
                      Gift MU
                    </Button>

                    <Button 
                      leftIcon={<FaUserMinus style={{ color: 'red' }} />}
                      bg="gray.700"
                      color="white"
                      size="sm"
                      onClick={() => openRemoveModal(friend)}
                      borderRadius="full"
                      width="100%"
                      px={4}
                      _hover={{ 
                        transform: "translateY(-2px)", 
                        boxShadow: "md",
                        bg: "gray.600"
                      }}
                      transition="all 0.2s"
                    >
                      Remove
                    </Button>
                  </VStack>
                </Flex>
              </Flex>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Confirmation Modal for removing friend */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered motionPreset="slideInBottom">
        <ModalOverlay 
          bg="blackAlpha.700"
          backdropFilter="blur(3px)"
        />
        <ModalContent 
          bg="gray.700"
          borderTop="4px solid"
          borderColor="red.500"
          borderRadius="lg"
          boxShadow="xl"
          color="white"
        >
          <ModalHeader 
            display="flex" 
            alignItems="center" 
            color="red.400"
            fontWeight="bold"
          >
            <FaUserMinus style={{ marginRight: '10px' }} /> Remove Kin
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={5}>
            <VStack spacing={4} align="center">
              <BurrowAvatar 
                size="xl" 
                src={selectedFriend?.userAvatar} 
                name={selectedFriend?.friendBurrowerId || "Kin"}
                border="3px solid"
                borderColor="yellow.400"
                boxShadow="0 0 15px rgba(236, 201, 75, 0.5)"
              />
              <Text fontSize="xl" fontWeight="bold" textAlign="center">
                {selectedFriend?.friendBurrowerId}
              </Text>
              <Text textAlign="center" color="gray.300">
                Are you sure you want to remove this kin? This action cannot be undone.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="gray" 
              mr={3} 
              onClick={onClose}
              variant="outline"
              _hover={{
                bg: "gray.600"
              }}
            >
              Cancel
            </Button>
            <Button 
              bg="gray.700"
              color="white"
              leftIcon={<FaUserMinus color="red.500" />}
              onClick={handleRemoveFriend}
              _hover={{
                bg: "gray.600", 
                shadow: "md"
              }}
              transition="all 0.2s"
            >
              Remove
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Message Modal */}
      <SendMessageModal
        isOpen={isMessageModalOpen}
        onClose={() => setIsMessageModalOpen(false)}
        initialRecipient={messageRecipient}
      />
      
      {/* Gift Credits Modal */}
      <GiftCreditsModal
        isOpen={isGiftModalOpen}
        onClose={() => setIsGiftModalOpen(false)}
        recipientId={giftRecipient}
      />

      {/* Avatar Preview Modal */}
      <Modal 
        isOpen={avatarPreviewModal.isOpen} 
        onClose={avatarPreviewModal.onClose} 
        size="3xl"
        motionPreset="slideInBottom"
        isCentered
      >
        <ModalOverlay 
          bg="blackAlpha.700"
          backdropFilter="blur(5px)"
        />
        <ModalContent bg="transparent" shadow="none" maxW="90vw">
          <ModalCloseButton 
            color="white" 
            bg="blackAlpha.700" 
            borderRadius="full"
            size="lg"
            _hover={{ bg: "blackAlpha.800" }}
          />
          <ModalBody display="flex" justifyContent="center" alignItems="center" p={8}>
            <Box
              p={6}
              borderRadius="xl"
              bg="gray.800"
              borderWidth="3px"
              borderColor="yellow.400"
              overflow="hidden"
              shadow="2xl"
              maxW="95%"
              transition="all 0.3s"
              position="relative"
              _before={{
                content: '""',
                position: "absolute",
                inset: "0",
                bgGradient: "linear(to-r, yellow.400, orange.400, yellow.500)",
                opacity: "0.1",
                borderRadius: "xl"
              }}
            >
              <Image
                src={previewAvatar || "https://bunny-burrow-pull-1.b-cdn.net/user-profile-images/image%20(16).png"}
                alt={previewUserName || "User"}
                borderRadius="lg"
                maxH="80vh"
                minH="400px"
                minW="400px"
                objectFit="contain"
                boxShadow="lg"
                transition="transform 0.5s"
                _hover={{ transform: "scale(1.02)" }}
              />
              <Box
                position="absolute"
                bottom="10px"
                left="50%"
                transform="translateX(-50%)"
                bg="blackAlpha.600"
                color="white"
                borderRadius="md"
                py={2}
                px={4}
                fontWeight="bold"
                backdropFilter="blur(5px)"
              >
                {previewUserName}
              </Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FriendsListTab;
